import { Pause, Play, RotateCw, Volume2, VolumeX } from 'lucide-react';
import { useEffect, useRef, useState, type PointerEvent } from 'react';
import { useFolderVideo } from '../../lib/useFolderVideo';
import type { PreviewSource } from './FilePreview';

const time = (value: number) => `${Math.floor(value / 60)}:${Math.floor(value % 60).toString().padStart(2, '0')}`;

/** Public read-only counterpart of Android Explorer's mobile-player. */
export function FolderMobileVideo({ file, onNavigate, onQuiet }: { file: PreviewSource; onNavigate: (direction: number) => void; onQuiet: (quiet: boolean) => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const stream = useFolderVideo(video, file.original);
  const [playing, setPlaying] = useState(false), [muted, setMuted] = useState(false);
  const [position, setPosition] = useState(0), [duration, setDuration] = useState(0);
  const [visible, setVisible] = useState(true), [activity, setActivity] = useState(0);
  const swipe = useRef<{ x: number; y: number; id: number } | null>(null);
  const quiet = !visible && playing && !stream.error && !stream.status;
  useEffect(() => { onQuiet(quiet); return () => onQuiet(false); }, [quiet, onQuiet]);
  useEffect(() => {
    if (!playing || stream.status || stream.error) return;
    const timer = setTimeout(() => setVisible(false), 3000);
    return () => clearTimeout(timer);
  }, [playing, activity, stream.status, stream.error]);
  const show = () => { setVisible(true); setActivity(value => value + 1); };
  const toggle = () => {
    show(); const element = video.current; if (!element) return;
    if (element.paused) void element.play().catch(() => {}); else element.pause();
  };
  const down = (event: PointerEvent<HTMLDivElement>) => {
    if (!event.isPrimary || (event.target as HTMLElement).closest('button,input,a')) { swipe.current = null; return; }
    swipe.current = { x: event.clientX, y: event.clientY, id: event.pointerId };
    event.currentTarget.setPointerCapture(event.pointerId);
  };
  const up = (event: PointerEvent<HTMLDivElement>) => {
    const start = swipe.current; swipe.current = null;
    if (!start || start.id !== event.pointerId) return;
    const dx = event.clientX - start.x, dy = event.clientY - start.y;
    if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5) onNavigate(dx < 0 ? 1 : -1);
    else if (Math.hypot(dx, dy) < 12) { if (quiet) show(); else setVisible(false); }
  };
  const update = () => {
    const element = video.current; if (!element) return;
    setPosition(Number.isFinite(element.currentTime) ? element.currentTime : 0);
    setDuration(Number.isFinite(element.duration) ? element.duration : 0);
  };
  return <div className={`sx-folder-video ${quiet ? 'is-quiet' : ''}`} onPointerDown={down} onPointerUp={up} onPointerCancel={() => { swipe.current = null; }}>
    <video ref={video} playsInline preload="none" aria-label={file.name} onPlay={() => setPlaying(true)} onPause={() => setPlaying(false)} onEnded={() => { setPlaying(false); show(); }} onTimeUpdate={update} onDurationChange={update}/>
    {!playing && !stream.status && !stream.error && <button type="button" className="sx-folder-video-play" aria-label="Воспроизвести" onClick={toggle}><Play size={32}/></button>}
    {(stream.status || stream.error) && <div className="sx-folder-video-status" role={stream.error ? 'alert' : 'status'}>
      <span>{stream.error || stream.status}</span>{stream.error && <button type="button" onClick={() => { show(); stream.retry(); }}><RotateCw size={18}/>Повторить</button>}
    </div>}
    <div className="sx-folder-video-controls" onFocusCapture={show} onPointerDown={show} inert={quiet}>
      <div className="sx-folder-video-buttons">
        <button type="button" aria-label={playing ? 'Пауза' : 'Воспроизвести'} onClick={toggle}>{playing ? <Pause size={22}/> : <Play size={22}/>}</button>
        <span>{time(position)} / {time(duration)}</span>
        <button type="button" aria-label={muted ? 'Включить звук' : 'Выключить звук'} onClick={() => { if (video.current) video.current.muted = !muted; setMuted(!muted); }}>{muted ? <VolumeX size={22}/> : <Volume2 size={22}/>}</button>
      </div>
      <input aria-label="Позиция видео" type="range" min={0} max={duration || 1} step={0.1} value={Math.min(position, duration || 1)} disabled={!duration} onChange={event => { const value = Number(event.target.value); if (video.current) video.current.currentTime = value; setPosition(value); show(); }}/>
    </div>
  </div>;
}
